/**
 * Calendar — where a drafted job appears without anyone typing it in.
 *
 * A page under General beside the rental system, and for the same
 * reason: it is answered on the day a Company starts and touched about
 * never after.
 *
 * Connecting is a grant rather than a key, which is the one way this
 * differs from every other credential the product holds (ADR-0028).
 * There is nothing to paste and so nothing to typo — what there is
 * instead is a trip to Google or Microsoft and back, so the buttons
 * leave the page rather than submitting a form.
 *
 * What we create is a calendar of our own inside their account. The
 * screen says so plainly, because it is the answer to the question
 * anybody would ask about this: no, it does not write into the diary you
 * run your business on.
 *
 * Nothing ever reads a stored grant back.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Banner } from "../../components/ui/Banner";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { GoogleMark, MicrosoftMark } from "../../components/ui/icons";
import { useSuccessToast } from "../../components/ui/Toast";
import {
  authorizeCalendar,
  disconnectCalendar,
  getCalendar,
  type CalendarConnection,
  type CalendarProvider,
} from "../../lib/settings-api";
import { errorMessage } from "../../lib/error-message";
import { CALENDAR_NAMES, readCalendar } from "../../lib/connections";
import { useAction } from "../../lib/use-action";
import { useSettingsSource } from "../../lib/use-settings-source";
import { Loading, SectionHeader } from "./section-chrome";
import styles from "./CalendarSection.module.css";

const PROVIDER_MARKS: Partial<
  Record<string, (props: { className?: string }) => React.JSX.Element>
> = {
  google: GoogleMark,
  microsoft: MicrosoftMark,
};

/**
 * What the callback says when it sends the browser back here. Each is a
 * thing the person can do something about, so each says what happened
 * rather than naming the step that failed.
 */
const CONNECT_ERRORS: Record<string, string> = {
  declined: "The connection was cancelled.",
  expired: "That took too long. Start connecting again.",
  invalid: "That link was not complete. Start connecting again.",
  unavailable: "That calendar is not available right now.",
  exchange: "Your calendar would not confirm the connection. Try again.",
  calendar: "We could not create the HireOS calendar in that account.",
  store: "The connection could not be saved. Try again.",
};

export function CalendarSection() {
  const {
    data: calendar,
    setData: setCalendar,
    loadError,
  } = useSettingsSource(getCalendar);
  const [searchParams, setSearchParams] = useSearchParams();

  // The callback lands back on this page with its verdict in the query
  // string. Read once and taken out of the URL, so a reload is not the
  // same message again.
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const toast = useSuccessToast();

  useEffect(() => {
    const failed = searchParams.get("error");
    const connected = searchParams.get("connected");
    if (!failed && !connected) return;
    setCallbackError(
      failed ? (CONNECT_ERRORS[failed] ?? CONNECT_ERRORS.invalid) : null,
    );
    if (connected) {
      toast("Calendar connected. New enquiries will appear in it from now on.");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("error");
    next.delete("connected");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  const connected = calendar !== null && readCalendar(calendar).state !== "none";

  return (
    <section>
      <SectionHeader
        title="Calendar"
        description="Where drafted jobs appear in the calendar you already use."
      />

      {loadError && (
        <Banner variant="error" className={styles.notice}>
          {loadError}
        </Banner>
      )}
      {callbackError && (
        <Banner variant="error" className={styles.notice}>
          {callbackError}
        </Banner>
      )}

      {!calendar ? (
        <Loading />
      ) : connected ? (
        <ConnectedState calendar={calendar} onDisconnected={setCalendar} />
      ) : (
        <ConnectChoice calendar={calendar} />
      )}
    </section>
  );
}

/** What a working (or broken) connection reads as, and the way out. */
function ConnectedState({
  calendar,
  onDisconnected,
}: {
  calendar: CalendarConnection;
  onDisconnected: (next: CalendarConnection) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const {
    working,
    error,
    setError,
    run,
  } = useAction();

  const disconnect = async () => {
    await run(async () => {
      onDisconnected(await disconnectCalendar());
      setConfirming(false);
    });
  };

  return (
    <div>
      {/* A grant expires where a key does not (ADR-0028), so this reads
          as something to redo rather than something to look into. */}
      {calendar.status === "broken" && (
        <Banner variant="warning" className={styles.notice}>
          Your calendar refused the last job we tried to add. Connect it again
          to start them appearing.
        </Banner>
      )}

      <dl className={styles.facts}>
        <Fact
          label="Calendar"
          value={readCalendar(calendar).name}
        />
        <Fact label="Account" value={calendar.account || "Not set"} />
        <Fact label="Writes to" value="HireOS — Enquiries" />
      </dl>

      <p className={styles.note}>
        Drafted jobs go into a HireOS calendar inside your account, never the
        one you already keep. Untick it in your calendar app to hide them all.
      </p>

      <div className={styles.actions}>
        <Button size="sm" variant="danger" onClick={() => setConfirming(true)}>
          Disconnect
        </Button>
      </div>

      <ConfirmModal
        open={confirming}
        title="Disconnect this calendar?"
        message="The HireOS calendar and its entries stay in your account. New jobs stop appearing."
        confirmLabel="Disconnect"
        loading={working}
        error={error}
        onConfirm={() => void disconnect()}
        onCancel={() => {
          setConfirming(false);
          setError(null);
        }}
      />
    </div>
  );
}

/**
 * The connect. A button per provider this deployment holds credentials
 * for, and nothing else: there is no field to fill in, because the grant
 * is made at the other end.
 */
function ConnectChoice({ calendar }: { calendar: CalendarConnection }) {
  const [working, setWorking] = useState<CalendarProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = async (provider: CalendarProvider) => {
    setWorking(provider);
    setError(null);
    try {
      window.location.href = await authorizeCalendar(provider);
    } catch (err) {
      setError(errorMessage(err));
      setWorking(null);
    }
  };

  if (calendar.offers.length === 0) {
    return (
      <Banner variant="info" className={styles.notice}>
        Calendar connections are not available on this deployment.
      </Banner>
    );
  }

  return (
    <div>
      <p className={styles.note}>
        Drafted jobs appear in a HireOS calendar we add. The one you keep is
        untouched.
      </p>

      {error && (
        <Banner variant="error" className={styles.notice}>
          {error}
        </Banner>
      )}

      {/* A row per provider, shaped as a setting row: the mark and the
          name on the left, the one control on the right. */}
      <ul className={styles.providers}>
        {calendar.offers.map((provider) => {
          const Mark = PROVIDER_MARKS[provider];
          const label = CALENDAR_NAMES[provider] ?? provider;
          return (
            <li key={provider} className={styles.provider}>
              <span className={styles.providerName}>
                {Mark && <Mark className={styles.providerMark} />}
                {label}
              </span>
              <Button
                size="sm"
                variant="secondary"
                loading={working === provider}
                onClick={() => void start(provider)}
              >
                Connect
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}
